import i18n from 'i18next';
import { translateDepartment } from './translationUtils';
import { uiText, uiMessage, getUiLocale, formatUiDate } from './uiTranslation';

// Translation metadata is additive. IDs, enums, database paths and original
// payload fields retain their existing meaning for older consumers.
export const systemMessage = (kind, params = {}, language = 'en') => {
  let title;
  let message;
  const p = { ...params };
  switch (kind) {
    case 'approval_decision':
      title = params.title || (params.approvalType === 'offer_letter'
        ? (params.status === 'approved' ? 'Offer approval approved' : 'Offer approval rejected')
        : (params.status === 'approved' ? 'Job approval approved' : 'Job approval rejected'));
      message = params.status === 'approved' ? '{{entity}} was approved by {{name}}.' : '{{entity}} was rejected by {{name}}.';
      break;
    case 'approval_request':
      title = params.approvalType === 'job_posting' ? 'Job approval request' : 'Offer approval request';
      message = '{{entity}} • Requested by {{name}}';
      break;
    case 'onboarding_task':
      title = 'Onboarding task due today';
      message = 'Onboarding task due today • {{name}}';
      break;
    case 'applicant_note_mention':
      title = 'You were mentioned in a candidate note';
      message = '{{author}} mentioned you in a note about {{candidate}}.';
      break;
    case 'internal_mobility':
      title = 'Internal Mobility';
      message = 'Internal mobility updated to {{role}} / {{department}}';
      break;
    case 'pto_decision':
      title = params.action === 'approve' ? 'PTO Request Approved' : 'PTO Request Rejected';
      message = params.action === 'approve'
        ? 'Your PTO request for {{start}} to {{end}} has been approved by {{name}}.'
        : 'Your PTO request for {{start}} to {{end}} has been rejected by {{name}}.';
      break;
    case 'review_scheduled':
      title = 'Performance Review Scheduled';
      message = 'A {{type}} review has been scheduled for {{date}} at {{time}}.';
      break;
    default:
      return {};
  }
  const localization = { kind, params: p };
  const text = renderSystemMessage(localization, language, message);
  const displayTitle = kind === 'onboarding_task' && params.taskTitle ? params.taskTitle : uiText(title, { lng: language });
  const displayText = kind === 'onboarding_task' && params.taskTitle ? `${params.taskTitle} • ${text}` : text;
  return { title: displayTitle, message: text, text: displayText, localization };
};

const renderSystemMessage = ({ kind, params }, language, template) => {
  const values = { ...params, lng: language };
  if (kind === 'internal_mobility') values.department = translateDepartment(params.department, language);
  if (kind === 'pto_decision') {
    values.start = formatUiDate(params.start, {}, language);
    values.end = formatUiDate(params.end, {}, language);
  }
  if (kind === 'review_scheduled') {
    values.type = uiText(params.type, { lng: language });
    values.date = formatUiDate(params.date, {}, language);
  }
  return uiText(template, values);
};

export const localizeSystemRecord = (record, language = getUiLocale()) => {
  if (!record || typeof record !== 'object') return record;
  let metadata = record.localization;
  // Known older records already contain safe structured facts. No matching or
  // translation of arbitrary user-authored prose is attempted.
  if (!metadata && record.type === 'approval_decision' && record.entityName && record.approver?.name) {
    metadata = { kind: record.type, params: { entity: record.entityName, name: record.approver.name, status: record.status, approvalType: record.approvalType } };
  }
  if (!metadata && record.type === 'onboarding_task' && record.hireName) {
    metadata = { kind: record.type, params: { name: record.hireName, taskTitle: record.title === 'Onboarding task due today' ? '' : record.title || '' } };
  }
  if (!metadata && record.type === 'applicant_note_mention') {
    const match = String(record.message || record.text || '').match(/^(.*?) mentioned you in a note about (.*?)\.$/);
    if (match) metadata = { kind: record.type, params: { author: match[1], candidate: match[2] } };
  }
  if (!metadata && record.type === 'approval_request' && record.entityName) {
    const prefix = `${record.entityName} • Requested by `;
    const text = record.message || record.text || '';
    if (text.startsWith(prefix)) metadata = { kind: record.type, params: { entity: record.entityName, name: text.slice(prefix.length), approvalType: record.approvalType } };
  }
  if (!metadata && record.type === 'internal_mobility') {
    const match = String(record.description || '').match(/^Internal mobility updated to (.*?) \/ (.*?)$/);
    if (match) metadata = { kind: record.type, params: { role: match[1], department: match[2] } };
  }
  if (!metadata?.kind) return record;
  const translated = systemMessage(metadata.kind, metadata.params, language);
  if (!translated.localization) return record;
  return { ...record, ...translated, ...(record.type === 'internal_mobility' ? { description: translated.message } : {}) };
};

export const systemRecordText = (record, field, language = getUiLocale()) => localizeSystemRecord(record, language)?.[field];

export const localizeApplicantNotification = (record, language = getUiLocale()) => {
  const tr = (key, values = {}) => i18n.t(key, { ns: 'applicants', lng: language, ...values });
  const type = record.type;
  if (!['unread_chat', 'assessment_completed', 'unmoved_candidate', 'logistics_incomplete', 'checklist_reminder'].includes(type)) return localizeSystemRecord(record, language);
  const candidate = record.candidateName || uiText('A candidate', { lng: language });
  let duration = record.duration || record.timeSince || '';
  if (Number.isFinite(record.durationDays)) {
    const days = record.durationDays;
    duration = days >= 30 ? tr('notifications.duration.months', { count: Math.floor(days / 30) })
      : days >= 14 ? tr('notifications.duration.weeks', { count: Math.floor(days / 7) })
        : tr('notifications.duration.overWeek');
  }
  let day = record.day || '';
  if (record.dayDate && record.dayIndex) {
    day = uiText('Day {{count}} ({{date}})', { count: record.dayIndex, date: formatUiDate(record.dayDate, { weekday: 'long', month: 'short', day: 'numeric' }, language), lng: language });
  }
  return {
    ...record,
    title: tr(`notifications.types.${type}.title`),
    description: tr(`notifications.types.${type}.description`, {
      candidate, assessment: record.assessmentTitle || record.assessment || '',
      duration, remaining: record.remainingTasks ?? record.remaining ?? 0, day,
    }),
  };
};

// Descriptor convenience for asynchronous UI status messages.
export { uiMessage };
